import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RotateCw, Clock, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminWarmerButton — generic, configurable admin warmer button.
 *
 * Drives any backend endpoint group that follows the new "warmer"
 * convention (Round 92, Feb 2026):
 *   GET  /api/admin/{prefix}/status
 *   POST /api/admin/{prefix}/fire
 *   POST /api/admin/{prefix}/stop
 *   POST /api/admin/{prefix}/reset-cooldown
 *
 * Renders a compact pill with:
 *   - icon + short label
 *   - cooldown countdown (when in cooldown)
 *   - inline progress counter "253 / 4,000" (when running)
 *   - last-run summary on hover
 *   - confirm dialog before firing
 *
 * Props:
 *   prefix    : endpoint prefix (e.g. "ratios-refresh")
 *   label     : visible label
 *   testid    : data-testid root
 *   icon      : lucide icon component
 *   tone      : "amber" | "violet" | "emerald" | "sky"
 *   description : 1-line description shown in confirm modal
 */
function formatCooldown(seconds) {
  if (seconds <= 0) return '0m';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d >= 1) return `${d}d ${h}h`;
  if (h >= 1) return `${h}h ${m}m`;
  return `${m}m`;
}

const TONE = {
  amber:   'border-amber-400/60 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-700/40 dark:hover:bg-amber-900/40',
  violet:  'border-violet-400/60 bg-violet-50 text-violet-900 hover:bg-violet-100 dark:bg-violet-950/40 dark:text-violet-200 dark:border-violet-700/40 dark:hover:bg-violet-900/40',
  emerald: 'border-emerald-400/60 bg-emerald-50 text-emerald-900 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-700/40 dark:hover:bg-emerald-900/40',
  sky:     'border-sky-400/60 bg-sky-50 text-sky-900 hover:bg-sky-100 dark:bg-sky-950/40 dark:text-sky-200 dark:border-sky-700/40 dark:hover:bg-sky-900/40',
};

export default function AdminWarmerButton({
  prefix,
  label,
  testid,
  icon: Icon,
  tone = 'violet',
  description,
}) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/${prefix}/status`, { withCredentials: true });
      setStatus(data);
    } catch { /* silent on poll */ }
  }, [prefix]);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    fetchStatus();
    const id = setInterval(fetchStatus, status?.in_progress ? 4000 : 30000);
    return () => clearInterval(id);
  }, [user, fetchStatus, status?.in_progress]);

  if (user?.role !== 'admin') return null;

  const inProgress = status?.in_progress;
  const cooldownRemaining = status?.cooldown_remaining_seconds || 0;
  const canRun = status?.can_run;

  const handleFire = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/${prefix}/fire`, {}, { withCredentials: true });
      toast.success(`${label} started`);
      setConfirmOpen(false);
      fetchStatus();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.cooldown_remaining_seconds) {
        toast.error(`Cooldown active — ${formatCooldown(detail.cooldown_remaining_seconds)} left`);
      } else {
        toast.error(`Could not start ${label}`);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(
      `Reset "${label}"?\n\n` +
      `• Clears the cooldown timer (next run fires immediately)\n` +
      `• ALSO clears any stuck in-progress state from a crashed worker\n\n` +
      `If a real run is actively making progress, hitting this kills it.`
    )) return;
    try {
      await axios.post(`${API}/admin/${prefix}/reset-cooldown`, {}, { withCredentials: true });
      toast.success('Cooldown + stuck state cleared');
      fetchStatus();
    } catch { toast.error('Reset failed'); }
  };

  const handleForceClear = async () => {
    if (!window.confirm(
      `Force-clear the stuck in-progress state for "${label}"?\n\n` +
      `Use this only when a previous run crashed mid-loop (Mongo blip, ` +
      `backend restart) and the spinner is stuck forever. This does NOT ` +
      `reset the cooldown.`
    )) return;
    try {
      await axios.post(`${API}/admin/${prefix}/force-clear`, {}, { withCredentials: true });
      toast.success('In-progress state cleared');
      fetchStatus();
    } catch { toast.error('Force-clear failed'); }
  };

  const labelText = inProgress
    ? `${label}: ${status.in_progress_processed ?? 0} / ${status.in_progress_total ?? '?'}`
    : !canRun && cooldownRemaining > 0
      ? `${label}: ${formatCooldown(cooldownRemaining)} left`
      : label;

  // Build a hover tooltip that always includes the description (when
  // provided) AND the last-run summary when available. Previously the
  // title attribute only ever showed "Last completed..." or "No prior
  // run" — leaving admins guessing what a button actually does.
  const tooltipLines = [];
  if (description) tooltipLines.push(description);
  if (status?.last_completed_at) {
    const ts = new Date(status.last_completed_at).toLocaleString();
    const proc = status.last_processed ?? '—';
    const nodat = status.last_no_data;
    const err = status.last_errored ?? '—';
    const ndPart = (typeof nodat === 'number') ? ` · no_data ${nodat}` : '';
    tooltipLines.push(`Last completed: ${ts} · processed ${proc}${ndPart} · errored ${err}`);
  } else if (!inProgress) {
    tooltipLines.push('No prior run.');
  }
  if (inProgress) {
    tooltipLines.push(
      `Running… ${status?.in_progress_processed ?? 0} of ${status?.in_progress_total ?? '?'} done.`
    );
  }
  const titleAttr = tooltipLines.join('\n');

  return (
    <>
      {/* Round 98k (Jul 2026) — Wrap main button + optional sibling
          controls (cooldown reset · force-clear) in a single flex
          container so the grid layout on Dashboard treats them as
          ONE cell. Without this wrapper each sibling collapsed into
          its own grid column, breaking the "one row per warmer"
          alignment across the Admin panel groups. */}
      <div className="inline-flex items-center gap-0 w-full" data-testid={`${testid}-cell`}>
      <button
        type="button"
        onClick={() => setConfirmOpen(true)}
        disabled={!canRun || busy || inProgress}
        className={`flex-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[11px] font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${TONE[tone] || TONE.violet}`}
        data-testid={`${testid}-fire`}
        title={titleAttr}
      >
        {inProgress ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : Icon ? <Icon className="w-3.5 h-3.5" /> : null}
        <span data-testid={`${testid}-label`}>{labelText}</span>
        {!canRun && cooldownRemaining > 0 && !inProgress && (
          <Clock className="w-3 h-3 opacity-70" />
        )}
        {canRun && status?.last_completed_at && (
          <CheckCircle2 className="w-3 h-3 opacity-60" />
        )}
      </button>
      {!canRun && cooldownRemaining > 0 && (
        <button
          type="button"
          onClick={handleReset}
          className="inline-flex items-center justify-center w-6 h-6 rounded-md border border-violet-300/40 text-violet-700 dark:text-violet-300 hover:bg-violet-100/60 dark:hover:bg-violet-900/40 -ml-1"
          title="Reset cooldown"
          data-testid={`${testid}-reset`}
        >
          <RotateCw className="w-3 h-3" />
        </button>
      )}
      {inProgress && (
        <button
          type="button"
          onClick={handleForceClear}
          className="inline-flex items-center justify-center w-6 h-6 rounded-md border border-rose-300/40 text-rose-700 dark:text-rose-300 hover:bg-rose-100/60 dark:hover:bg-rose-900/40 -ml-1"
          title="Force-clear stuck in-progress state (only if spinner is stuck)"
          data-testid={`${testid}-force-clear`}
        >
          <RotateCw className="w-3 h-3" />
        </button>
      )}
      </div>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-[10010] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => !busy && setConfirmOpen(false)}
          data-testid={`${testid}-modal`}
        >
          <div
            className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 p-5 max-w-sm w-[92vw]"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-base mb-2 text-slate-900 dark:text-slate-100">
              Fire {label}?
            </h3>
            {description && (
              <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
                {description}
              </p>
            )}
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
              Cooldown: {Math.round(status?.cooldown_seconds / 3600)}h. Next eligible run starts after completion.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={busy}
                className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                data-testid={`${testid}-cancel`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleFire}
                disabled={busy}
                className="px-3 py-1.5 text-sm rounded-md bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60 inline-flex items-center gap-1.5"
                data-testid={`${testid}-confirm`}
              >
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Fire now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
